// setlist-rail (SP3) — real in-player setlist navigator.
//
// Mount target: <aside id="share-rail">. The only rail the player has: a
// published setlist (`/{shareId}?setlist=:id`) gives it the real items, a
// single shared song a synthesized one-item list (main.ts mountShell — the old
// single-song promo sidebar is gone). Renders the ordered items, highlights the active
// song (read from shared `state.activeShareId`), and emits `onSelect(shareId)`
// on click of any non-active row. Navigation (loadSong + pushState) is wired by
// the boot orchestrator — this component is pure: list + highlight + onSelect.
//
// Reuses the desktop `.sl-*` CSS (vendored in styles/desktop/setlist-panel.css)
// + web `.sl-*` additions in styles/setlist-sidebar.css.

import '@bandway/ui-components';
import '@bandway/ui-components/styles/bw-setlist-card.css';
import '@bandway/ui-components/styles/bw-tag.css';
import type { SetlistItem } from '../setlist.js';
import { subscribe } from '../state.js';
import type { AppState } from '../state.js';
import { t } from '../ui/i18n.js';
import { createRailLibraryFooter } from '../ui/rail-library-button.js';

export interface SetlistRailDeps {
  items: SetlistItem[];
  activeShareId: string | null;
  onSelect: (shareId: string) => void;
  /** Setlist name shown in the rail header. Falls back to a generic label. */
  name?: string | null;
  /** ISO createdAt — rendered as the header date (e.g. "4 Jun · 2"). */
  createdAt?: string | null;
}

/** Compact header meta mirroring desktop left-rail: "<day> <Mon> · <count>". */
function formatSetlistMeta(createdAt: string | null | undefined, count: number): string {
  const parts: string[] = [];
  if (createdAt) {
    const d = new Date(createdAt);
    if (!Number.isNaN(d.getTime())) {
      const mon = d.toLocaleString('en', { month: 'short' });
      parts.push(`${d.getDate()} ${mon}`);
    }
  }
  parts.push(String(count));
  return parts.join(' · ');
}

export interface SetlistRailHandle {
  unmount: () => void;
}

export function mountSetlistRail(host: Element, deps: SetlistRailDeps): SetlistRailHandle {
  const { items, onSelect } = deps;
  host.textContent = '';

  const panel = document.createElement('div');
  panel.className = 'sl-panel';
  panel.id = 'setlist-panel';

  // Header mirrors the desktop left-rail: small grey "SETLIST" label, white
  // setlist name (larger), then a grey date · song-count meta line.
  const header = document.createElement('header');
  header.className = 'sl-header';

  const label = document.createElement('div');
  label.className = 'sl-header-label';
  label.textContent = t('setlist.railHeading');
  header.appendChild(label);

  const name = document.createElement('div');
  name.className = 'sl-header-name';
  name.textContent = deps.name && deps.name.trim() ? deps.name : t('setlist.untitled');
  header.appendChild(name);

  const meta = document.createElement('div');
  meta.className = 'sl-header-meta';
  meta.textContent = formatSetlistMeta(deps.createdAt, items.length);
  header.appendChild(meta);

  panel.appendChild(header);

  const list = document.createElement('div');
  list.className = 'sl-list';
  list.setAttribute('role', 'list');

  const sorted = [...items].sort((a, b) => a.position - b.position);
  // Song rows use the product's DS card (bw-setlist-card) so the web member
  // player matches the desktop left-rail: position | name (+meta) | NOW chip.
  type Card = HTMLElement & {
    position: number;
    song: { name: string; key?: string; bpm?: number; durationSec?: number } | null;
    status: 'idle' | 'now' | 'next';
    nowLabel: string;
    mode: string;
  };
  const rowsByShareId = new Map<string, Card>();

  for (const it of sorted) {
    const card = document.createElement('bw-setlist-card') as Card;
    card.dataset.role = 'rail-song';
    card.dataset.shareId = it.shareId;
    card.position = it.position + 1;
    card.song = { name: it.songName ?? t('setlist.untitled') };
    card.mode = 'rehearsal'; // amber NOW chip, as in the member player
    card.nowLabel = t('setlist.nowBadge');   // DS default is English 'NOW'
    card.status = it.shareId === deps.activeShareId ? 'now' : 'idle';

    const select = (): void => {
      // No-op for the already-active song — avoids a needless reload.
      if (card.status === 'now') return;
      onSelect(it.shareId);
    };
    card.addEventListener('bw-setlist-card-select', select);

    rowsByShareId.set(it.shareId, card);
    list.appendChild(card);
  }
  panel.appendChild(list);

  // Back-to-library lives at the bottom of the rail (moved out of the cramped
  // player header). list has flex:1, so the footer pins to the bottom.
  panel.appendChild(createRailLibraryFooter());

  host.appendChild(panel);

  function applyActive(activeShareId: string | null): void {
    for (const [shareId, card] of rowsByShareId) {
      card.status = shareId === activeShareId ? 'now' : 'idle';
    }
  }
  applyActive(deps.activeShareId);

  // React to active-song changes from the shared store (loadSong sets it).
  // subscribe fires immediately with current state; fall back to the deps
  // value when the store hasn't recorded an active share yet, so the initial
  // highlight from `deps.activeShareId` is not clobbered on mount.
  const unsub = subscribe((s: AppState) => applyActive(s.activeShareId ?? deps.activeShareId));

  return {
    unmount: () => {
      unsub();
      host.textContent = '';
    },
  };
}
